/* eslint-disable new-cap */
/* eslint-disable max-depth */
import Button from "../../../components/Button";
import PageTitle from "../../../components/PageTitle";
import { useEffect, useState } from "react";
import { httprequest } from "../../../data/api";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useMediaQuery } from "@mui/material";
import Table from "../../../components/Table/Table";
import { TableSearch } from "../../../components/Table/TableActions";
import Badge from "../../../components/Badge";
import { useNavigate } from "react-router-dom";
import Modal from "../../../components/Modal";
import PageLoader from "../../../components/PageLoader";
import ReactDOMServer from "react-dom/server";
import PublishTemplateForPDF from "../../Reports/component/PublishTemplate";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const REPORT_ENDPOINT = "/api/v1/student-reports";

const ReportTemplate = ({ id }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  const getAllReport = async () => {
    setIsReportLoading(true);
    try {
      const res = await httprequest.get(`${REPORT_ENDPOINT}/${id}`);
      setReportData(res.data.data);
      setFilteredData(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch reports");
    } finally {
      setIsReportLoading(false);
    }
  };

  const handleViewReport = (row) => {
    if (row?.report_id) {
      navigate(`/report/viewreport/${row.report_id}`);
    } else if (row?.student_id) {
      const queryParams = new URLSearchParams();

      const termId = row.term_id ;
      if (termId) queryParams.append("term_id", termId);

      const classId = row.class_id || row.class?.id;
      if (classId) queryParams.append("class_id", classId);

      if (!termId) {
        toast.warning("Term ID is required to view this report");

        return;
      }

      navigate(`/report/viewreport/student/${row.student_id}?${queryParams.toString()}`);
    } else {
      toast.warning("No valid report or student identifier found");
    }
  };

  const generateReportPDF = async (reportId) => {
    let tempDiv = null;

    try {
    // Fetch report data
      const response = await httprequest.get(`/api/v1/student-report?report_ids=${reportId}`);
      const reportData = Array.isArray(response.data.data)
        ? response.data.data[0]
        : response.data.data;

      // Create temporary container
      tempDiv = document.createElement("div");
      tempDiv.style.width = "794px"; // A4 width in pixels
      tempDiv.style.padding = "20px";
      tempDiv.style.boxSizing = "border-box";
      document.body.appendChild(tempDiv);

      // Add critical CSS for PDF rendering
      const style = document.createElement("style");
      style.textContent = `
      * {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      table {
        border-collapse: collapse !important;
        width: 100% !important;
      }
      th, td {
        border: 1px solid #e0e0e0 !important;
        padding: 8px 12px !important;
      }
      thead th {
        background-color: #f9fafb !important;
        font-weight: 500 !important;
      }
    `;
      tempDiv.appendChild(style);

      // Render the component
      const htmlString = ReactDOMServer.renderToString(
        <PublishTemplateForPDF reportData={reportData} />
      );
      tempDiv.innerHTML = htmlString;

      // Wait for resources to load
      await new Promise(resolve => {
        const images = tempDiv.getElementsByTagName("img");
        if (images.length === 0) return resolve();

        let loaded = 0;
        const onLoad = () => {
          loaded++;
          if (loaded === images.length) resolve();
        };

        Array.from(images).forEach(img => {
          if (img.complete) onLoad();
          else img.addEventListener("load", onLoad);
          img.addEventListener("error", onLoad);
        });
      });

      // Add small delay to ensure rendering completes
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generate PDF
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: true,
        backgroundColor: "#FFFFFF",
        allowTaint: true,
        ignoreElements: (element) => {
        // Ignore elements that might interfere with table rendering
          return element.classList.contains("MuiDataGrid-menuIcon") ||
               element.classList.contains("MuiDataGrid-columnSeparator");
        }
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [794, 1123] // A4 dimensions
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const imgWidth = pdf.internal.pageSize.getWidth() - 40; // Add margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "JPEG", 20, 20, imgWidth, imgHeight);

      // Clean up
      document.body.removeChild(tempDiv);

      return pdf.output("blob");
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("PDF generation failed:", error);
      if (tempDiv && tempDiv.parentNode) {
        document.body.removeChild(tempDiv);
      }
      throw error;
    }
  };

  const handlePrintClick = async (report) => {
    try {
      setSelectedReport(report);
      setPrintModalOpen(true);

      const pdfBlob = await generateReportPDF(report.report_id);
      const pdfUrl = URL.createObjectURL(pdfBlob);

      setSelectedReport(prev => ({ ...prev, pdfUrl }));
    } catch (error) {
      toast.error(`Failed to generate PDF: ${error.message}`);
      setPrintModalOpen(false);
    }
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Name</p>}
          {row.document?.name}
        </>
      )
    },
    {
      name: "Class",
      selector: (row) => (
        <>
          {isMobile && <p className="mobile-styling">Class</p>}
          {row.class?.name}
        </>
      )
    },
    {
      name: "Status",
      center: true,
      cell: (row) => (
        <p style={{ display: "flex", flexDirection: "column" }}>
          {isMobile && <p style={{ margin: 0, fontSize: "12px" }}>Status</p>}
          <Badge status={row.status} />
        </p>
      )
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <button className="table-action-icon" onClick={() => handleViewReport(row)}>
            <Icon icon="hugeicons:view" />
          </button>
          {row.status === "published" ?
            <button className="table-action-icon" onClick={() => handlePrintClick(row)}>
              <Icon icon="fluent:print-32-regular" />
            </button>
            : ""}
        </div>
      )
    }
  ];
  useEffect(() => {
    setFilteredData(reportData);
  }, [reportData]);

  const handleSearch = (e) => {
    const { value } = e.target;

    if (!value) {
      setFilteredData(reportData);

      return;
    }

    const searchValue = value.toLowerCase();
    const searchTerms = searchValue.split(" ");

    const result = reportData.filter((report) => searchTerms.every(term => (report?.last_name && report.last_name.toLowerCase().includes(term)) ||
      (report?.first_name && report.first_name.toLowerCase().includes(term)) ||
      (report?.class?.name && report.class.name.toLowerCase().includes(term)) ||
      (report?.status && report.status.toLowerCase().includes(term))
    )
    );

    setFilteredData(result);
  };

  useEffect(() => {
    getAllReport();
  }, []);

  return (
    <>
      <PageTitle
        title="Student Report"
        subtitle="View students reports here."

      />

      {isReportLoading ? (
        <PageLoader />
      ) : (
        <Table
          data={filteredData}
          columns={columns}
          subHeader={true}
          pagination
          subHeaderComponent={
            <TableSearch
              placeholder="Search here..."
              searchTableFunc={handleSearch}
            />
          }
        />
      )}

      <Modal
        title="Print Report"
        subtitle={`Print report for ${selectedReport?.last_name || ""} ${selectedReport?.first_name || ""}`}
        isopen={printModalOpen}
        closeModal={() => {
          setPrintModalOpen(false);
          if (selectedReport?.pdfUrl) {
            URL.revokeObjectURL(selectedReport.pdfUrl);
          }
        }}
        modalContent={
          <div style={{ textAlign: "center", padding: "20px" }}>
            <p>Choose an action for this report:</p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "20px" }}>
              <Button
                variant="primaryoutline"
                buttonSize="medium"
                color="btnfontprimary"
                buttonName="View PDF"
                onClick={() => {
                  if (selectedReport?.pdfUrl) {
                    window.open(selectedReport.pdfUrl, "_blank");
                    setPrintModalOpen(false);
                  }
                }}
              />
              <Button
                variant="primary"
                buttonSize="medium"
                color="btndefault"
                buttonName="Download PDF"
                onClick={async () => {
                  try {
                    // Generate the PDF blob
                    const pdfBlob = await generateReportPDF(selectedReport.report_id);

                    // Create download link
                    const link = document.createElement("a");
                    const pdfUrl = URL.createObjectURL(pdfBlob);
                    link.href = pdfUrl;
                    link.download = `${selectedReport.last_name}_${selectedReport.first_name}_report.pdf`;
                    document.body.appendChild(link);
                    link.click();

                    // Clean up
                    setTimeout(() => {
                      document.body.removeChild(link);
                      URL.revokeObjectURL(pdfUrl);
                    }, 100);

                    setPrintModalOpen(false);
                  } catch (error) {
                    toast.error(`Failed to download PDF: ${error.message}`);
                  }
                }}
              />
            </div>
          </div>
        }
        modalFooter={
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="primary"
              buttonSize="full"
              color="btndefault"
              buttonName="Print Now"
              onClick={() => {
                if (selectedReport?.pdfUrl) {
                  const iframe = document.createElement("iframe");
                  iframe.style.display = "none";
                  iframe.src = selectedReport.pdfUrl;
                  document.body.appendChild(iframe);
                  iframe.onload = () => {
                    iframe.contentWindow.print();
                  };
                  setPrintModalOpen(false);
                }
              }}
            />
          </div>
        }
      />

      {/* <ToastContainer /> */}
    </>
  );
};

export default ReportTemplate;
